import { computed, onBeforeUnmount, onScopeDispose, onMounted, readonly, ref, shallowRef, watch, type Ref, type InjectionKey } from 'vue';
import { fetchWithAuth, getApiOrigin, readBearer, readShellAccount, readShellAccountId } from './fetch';

export type AccountService = 'mail' | 'calendar';
export const accountSelectionKey: InjectionKey<ReturnType<typeof useAccountSelection>> = Symbol('account-selection');
export interface SharedAccount {
  uuid: string;
  email: string;
  name: string;
  services: AccountService[];
}
export interface AccountSelectionSnapshot {
  scope: { sessionUUID: string; actorUUID: string; tenantID: number };
  accounts: SharedAccount[];
  validUntil: string;
}

const snapshot = shallowRef<AccountSelectionSnapshot | null>(null);
const selected = ref('');
const generation = ref(0);
const pending = ref(true);
const notice = ref<'unavailable' | 'changed' | ''>('');
const locks = ref(0);
let controller = new AbortController();
let requestVersion = 0;
let refreshPromise: Promise<void> | null = null;
let expiryTimer: ReturnType<typeof setTimeout> | undefined;
let refreshTimer: ReturnType<typeof setInterval> | undefined;
let users = 0;
let snapshotOrigin = '';
const currentIdentity = () => JSON.stringify([getApiOrigin(), actor(), Boolean(readBearer())]);
let observedIdentity = currentIdentity();
const revokeListeners = new Set<(namespace: string) => void>();

function actor(): string { return readShellAccountId(); }
function ownScopeStorageKey(): string { return `formica.accountScope:${JSON.stringify([getApiOrigin(), actor()])}`; }
/** The tenant the actor's accounts were last authorized in; 0 while unknown. */
function knownTenant(): number {
  if (snapshot.value?.scope.actorUUID === actor()) return snapshot.value.scope.tenantID;
  try {
    const stored = JSON.parse(localStorage.getItem(ownScopeStorageKey()) || '{}');
    if (Number.isInteger(stored.tenantID)) return stored.tenantID;
  } catch { /* storage may be unavailable */ }
  return 0;
}
function scopeKey(value = snapshot.value): string {
  return JSON.stringify([getApiOrigin(), value ? value.scope.tenantID : knownTenant(), actor()]);
}
function delegated(owner: string): boolean { return Boolean(owner && owner !== actor()); }
function fresh(): boolean {
  const value = snapshot.value;
  return Boolean(value && value.scope.actorUUID === actor() && snapshotOrigin === getApiOrigin() && Date.parse(value.validUntil) > Date.now());
}
function fence(): void {
  controller.abort();
  controller = new AbortController();
  generation.value++;
}
function revokePrevious(next: AccountSelectionSnapshot | null): void {
  const previous = snapshot.value;
  if (!previous) return;
  for (const account of previous.accounts) {
    if (account.uuid === previous.scope.actorUUID) continue;
    for (const service of account.services) {
      if (next && scopeKey(next) === scopeKey(previous) && next.accounts.some((item) => item.uuid === account.uuid && item.services.includes(service))) continue;
      const namespace = JSON.stringify([snapshotOrigin, previous.scope.tenantID, previous.scope.actorUUID, account.uuid, service]);
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key?.endsWith(`:${namespace}`)) localStorage.removeItem(key);
        }
      } catch { /* inaccessible storage cannot be read by this client either */ }
      for (const listener of revokeListeners) listener(namespace);
    }
  }
}
function expire(): void {
  const wasDelegated = delegated(selected.value);
  const previousGeneration = generation.value;
  revokePrevious(null);
  if (snapshot.value) snapshot.value = { ...snapshot.value, accounts: snapshot.value.accounts.filter((item) => item.uuid === actor()) };
  if (delegated(selected.value)) { selected.value = actor(); notice.value = 'changed'; }
  if (wasDelegated && previousGeneration === generation.value) fence();
}

/** The server, never persisted browser grants, authorizes the account list. */
export async function refreshAccountSelection(): Promise<void> {
  if (refreshPromise) return refreshPromise;
  const version = requestVersion;
  const identity = actor();
  const origin = getApiOrigin();
  refreshPromise = (async () => {
    try {
      // This discovery gates the workspace on cold startup. The periodic poll
      // already retries; exponential network retries here leave even the own
      // offline cache behind a blank screen for several seconds.
      const response = await fetchWithAuth(`${origin}/v1/account/available_accounts`, {
        cache: 'no-store', headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(5_000),
      }, { maxRetries: 0 });
      if (!response.ok) throw new Error('Account authorization unavailable');
      const value = await response.json() as AccountSelectionSnapshot;
      if (version !== requestVersion || actor() !== identity || origin !== getApiOrigin()) return;
      if (value.scope?.actorUUID !== identity || !Array.isArray(value.accounts) ||
          !value.accounts.some((item) => item.uuid === identity) || !Number.isFinite(Date.parse(value.validUntil)) || Date.parse(value.validUntil) <= Date.now()) {
        throw new Error('Invalid account authorization');
      }
      const changedScope = scopeKey(value) !== scopeKey();
      revokePrevious(value);
      const oldAllowed = selected.value && value.accounts.some((item) => item.uuid === selected.value);
      value.validUntil = new Date(Math.min(Date.parse(value.validUntil), Date.now() + 30_000)).toISOString();
      snapshot.value = value;
      snapshotOrigin = origin;
      // Retain only the own-cache partition across offline reloads, never a
      // delegated account or an authorization snapshot.
      try { localStorage.setItem(ownScopeStorageKey(), JSON.stringify({ tenantID: value.scope.tenantID })); } catch { /* optional cache metadata */ }
      if (changedScope || !oldAllowed) {
        if (delegated(selected.value)) notice.value = 'changed';
        selected.value = identity;
        fence();
      }
      pending.value = false;
      if (notice.value === 'unavailable') notice.value = '';
      clearTimeout(expiryTimer);
      // Clamp even a malformed upstream expiration; cached delegation never gets
      // a longer local lease than the policy's thirty seconds.
      expiryTimer = setTimeout(expire, Math.min(30_000, Date.parse(value.validUntil) - Date.now()));
    } catch {
      if (version !== requestVersion) return;
      notice.value = 'unavailable';
      if (!fresh()) expire();
      pending.value = false;
    } finally { if (version === requestVersion) refreshPromise = null; }
  })();
  return refreshPromise;
}

function identityChanged(event?: Event): void {
  if (event?.type === 'storage') {
    const key = (event as StorageEvent).key;
    // Other tabs also write cache/preferences. While offline there may be no
    // snapshot; those changes must not erase the remembered own-cache scope.
    if (key !== null && !['blp_token', 'formica.oauth.session', 'formica.api.baseUrl'].includes(key)) return;
  }
  if (observedIdentity !== currentIdentity() || !readBearer()) invalidateAccountSelection();
  void refreshAccountSelection();
}
export function invalidateAccountSelection(): void {
  observedIdentity = currentIdentity();
  requestVersion++;
  refreshPromise = null;
  clearTimeout(expiryTimer);
  revokePrevious(null);
  try { localStorage.removeItem(ownScopeStorageKey()); } catch { /* optional cache metadata */ }
  snapshot.value = null;
  selected.value = '';
  pending.value = true;
  fence();
}
/** The session is moving to `tenantID`. Accounts, their shares and every cache
 * keyed here belong to one tenant, so only a move to another tenant ends the
 * selection (write fence, delegated caches); a tenant not known yet counts as
 * another one. Returns whether the selection was invalidated. */
export function invalidateAccountSelectionForTenant(tenantID: number): boolean {
  const known = knownTenant();
  if (known && known === tenantID) return false;
  invalidateAccountSelection();
  return true;
}
export function onAccountAccessRevoked(listener: (namespace: string) => void): () => void {
  revokeListeners.add(listener);
  return () => revokeListeners.delete(listener);
}
export function selectedAccount(service: AccountService): SharedAccount {
  const value = snapshot.value;
  const current = fresh() && value?.accounts.find((item) => item.uuid === selected.value && item.services.includes(service));
  if (current) return current;
  const personal = value?.scope.actorUUID === actor() && value.accounts.find((item) => item.uuid === actor());
  return personal || { uuid: actor(), name: readShellAccount()?.name || '', email: readShellAccount()?.email || '', services: ['mail', 'calendar'] };
}

/** Immutable lifetime for one mounted view / operation, including token refresh. */
export function captureAccountRequest(service: AccountService, owners: string[] = [selectedAccount(service).uuid]) {
  const version = generation.value;
  const scope = scopeKey();
  const signedIn = actor();
  const signal = controller.signal;
  const check = () => {
    if (version !== generation.value || scope !== scopeKey() || signedIn !== actor() || signal.aborted) throw new DOMException('Account context changed', 'AbortError');
    if (!signedIn) return;
    for (const owner of owners) {
      if (!owner || owner === signedIn) continue;
      if (!fresh() || !snapshot.value?.accounts.some((item) => item.uuid === owner && item.services.includes(service))) throw new DOMException('Account access expired', 'AbortError');
    }
  };
  check();
  const valid = () => { try { check(); return true; } catch { return false; } };
  const run = async <T>(operation: () => Promise<T>): Promise<T> => {
    check();
    try { const result = await operation(); check(); return result; }
    catch (error) { check(); throw error; }
  };
  return { check, signal, valid, run };
}

/** No bearer or session token in persistent keys. Each database belongs to one
 * tenant/actor/owner/service; `owner` defaults to the selected account. */
export function accountStorageNamespace(service: AccountService, owner = selectedAccount(service).uuid): string {
  if (!actor()) return ''; // isolated unit tests / no authenticated storage
  captureAccountRequest(service, [owner]).check();
  return JSON.stringify([getApiOrigin(), knownTenant(), actor(), owner, service]);
}

export function accountStorageKey(service: AccountService, key: string): string {
  const namespace = accountStorageNamespace(service);
  return namespace ? `${key}:${namespace}` : key;
}

export function useAccountSelection(service: AccountService) {
  const current = computed(() => { void generation.value; return selectedAccount(service); });
  const accounts = computed(() => {
    const list = fresh() ? snapshot.value?.accounts.filter((item) => item.services.includes(service)) || [] : [];
    return list.length ? list : [current.value];
  });
  const key = computed(() => `${scopeKey()}:${current.value.uuid}:${generation.value}`);
  watch(snapshot, (value) => {
    if (value && delegated(selected.value) && !value.accounts.some((item) => item.uuid === selected.value && item.services.includes(service))) {
      selected.value = actor();
      notice.value = 'changed';
      fence();
    }
  }, { flush: 'sync' });
  function choose(uuid: string): void {
    if (locks.value || !accounts.value.some((item) => item.uuid === uuid)) return;
    selected.value = uuid;
    notice.value = '';
    fence();
  }
  onMounted(() => {
    if (++users === 1) {
      window.addEventListener('formica:auth-changed', identityChanged);
      window.addEventListener('storage', identityChanged);
      window.addEventListener('online', identityChanged);
      refreshTimer = setInterval(() => { void refreshAccountSelection(); }, 15_000);
    }
    if (selected.value && current.value.uuid !== selected.value) { selected.value = current.value.uuid; notice.value = 'changed'; fence(); }
    void refreshAccountSelection();
  });
  onBeforeUnmount(() => {
    if (--users === 0) {
      clearInterval(refreshTimer);
      window.removeEventListener('formica:auth-changed', identityChanged);
      window.removeEventListener('storage', identityChanged);
      window.removeEventListener('online', identityChanged);
    }
  });
  return { current, accounts, key, choose, pending: readonly(pending), notice: readonly(notice), locked: computed(() => locks.value > 0), refresh: refreshAccountSelection };
}

/** Keep an open editor attached to its owner until it is saved or closed. */
export function holdAccountSelection(editing: Ref<boolean>): void {
  let held = false;
  watch(editing, (value) => { if (value !== held) { locks.value += value ? 1 : -1; held = value; } }, { immediate: true, flush: 'sync' });
  onScopeDispose(() => { if (held) locks.value--; });
}
